/**
 * CORE-1685 — одна эмодзи комплекта: картинка по слагу (старые слаги — через
 * сопоставление по смыслу), запасной символ для слага, которого у нас нет.
 * Общий для чипов реакций, палитры выбора и эмодзи в тексте.
 */
import React from 'react';
import { Image, Platform, Text, View, type ImageStyle, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { reactionOf } from '../../constants/brandReactions';
import { REACTION_EMOJI } from '../../services/chat/reactions';

interface Props {
  type: string;
  /** Сторона картинки в px. */
  size?: number;
  style?: StyleProp<ImageStyle>;
  /** Стиль запасного символа, когда картинки нет. */
  textStyle?: StyleProp<TextStyle>;
  /** Инлайн в тексте: картинка чуть опускается на базовую линию. */
  inline?: boolean;
  /**
   * CORE-1791 (QA 05.11) — имя уже у обёртки: пузырь из одних эмодзи называет
   * их все разом («Благодарность, Огонь»). Картинка внутри — без своего имени,
   * иначе каждая эмодзи звучит второй раз.
   */
  decorative?: boolean;
}

export const ReactionGlyph: React.FC<Props> = ({ type, size = 20, style, textStyle, inline, decorative }) => {
  const reaction = reactionOf(type);
  if (!reaction) {
    const fallback = REACTION_EMOJI[type] ?? '❤️';
    return <Text style={[{ fontSize: size * 0.8 }, textStyle]}>{fallback}</Text>;
  }
  const box = [
    { width: size, height: size },
    inline ? { transform: [{ translateY: size * 0.2 }] } : null,
    style,
  ];
  if (decorative) {
    return <Image source={reaction.image} style={box} resizeMode="contain" />;
  }
  if (Platform.OS === 'web') {
    // CORE-1791 (QA 05.11, 61539) — на вебе `Image` — это div с подписью и
    // скрытый <img alt> с той же подписью внутри. С ролью у div эмодзи
    // оказывалась в дереве доступности дважды («Благодарность» внутри
    // «Благодарность»). Имя и роль — у обёртки, картинка внутри без подписи
    // (alt=""), её дерево не видит. Без роли у div подпись была бы запрещённой
    // (возврат QA 04.11).
    return (
      <View accessible accessibilityRole="image" accessibilityLabel={reaction.hint} style={box as StyleProp<ViewStyle>}>
        <Image source={reaction.image} style={{ width: size, height: size }} resizeMode="contain" />
      </View>
    );
  }
  return (
    <Image
      source={reaction.image}
      // CORE-1791 (возврат QA 04.11) — картинка с именем для экранного диктора.
      accessibilityRole="image"
      accessibilityLabel={reaction.hint}
      style={box}
      resizeMode="contain"
    />
  );
};
